import React, { useState } from 'react';
import {
    Drawer,
    IconButton,
    Typography,
    Box,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider,
    List,
    ListItem,
    ListItemText,
    Toolbar,
    AppBar,
    Tabs,
    Tab,
    Paper,
    Link,
} from '@material-ui/core';
import { Link as RouterLink } from 'react-router-dom';
import { usePermissions } from 'react-admin';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import HelpIcon from '@material-ui/icons/Help';
import CloseIcon from '@material-ui/icons/Close';
import ListIcon from '@material-ui/icons/List';

const DRAWER_WIDTH = 800;
const TOC_WIDTH = 250;

const HelpDialog = ({ open, onClose }) => {
    const { permissions } = usePermissions();
    const role = permissions?.role || 'none';
    const [showTOC, setShowTOC] = useState(true);
    const [activeSection, setActiveSection] = useState('introduction');
    const [expandedSection, setExpandedSection] = useState('introduction');

    // Helper function to check if user has access to a resource
    const hasAccess = (resourceRoles) => {
        return resourceRoles.includes(role);
    };

    // Define resources with their access roles
    const availableResources = [
        {
            name: 'Students',
            path: '/Students',
            roles: ['admin', 'teacher', 'toddlerteacher', 'officemgr', 'toddlerassistant'],
            description: 'Student records, enrollment, and academic information'
        },
        {
            name: 'Applications',
            path: '/Applications',
            roles: ['admin', 'officemgr'],
            description: 'New student applications and enrollment requests'
        },
        {
            name: 'Tours',
            path: '/tours/TourDetails',
            roles: ['admin', 'officemgr'],
            description: 'School tour scheduling and management'
        },
        {
            name: 'Employees',
            path: '/Employees',
            roles: ['admin', 'officemgr'],
            description: 'Staff management and employee records'
        },
        {
            name: 'Schedule Updates',
            path: '/ScheduleUpdates',
            roles: ['admin', 'officemgr'],
            description: 'Scheduled changes to student programs, transitions, and enrollment status'
        },
        {
            name: 'Receipts',
            path: '/Receipts',
            roles: ['admin'],
            description: 'Payment receipts and financial records'
        },
        {
            name: 'Payments',
            path: '/Payments',
            roles: ['admin'],
            description: 'Payment processing and tracking'
        },
        {
            name: 'Summaries',
            path: '/Summaries',
            roles: ['admin', 'officemgr'],
            description: 'Financial summaries and reports'
        },
        {
            name: 'Student Attendance',
            path: '/StudentSignInOut',
            roles: ['admin', 'officemgr'],
            description: 'Student sign-in and sign-out records'
        },
        {
            name: 'Student Absences',
            path: '/StudentAbsences',
            roles: ['admin', 'officemgr'],
            description: 'Student absence tracking'
        },
        {
            name: 'Report Cards',
            path: '/ReportCards',
            roles: ['admin', 'teacher', 'toddlerteacher', 'officemgr'],
            description: 'Student progress reports'
        },
        {
            name: 'Master Report Card',
            path: '/MasterReportCards',
            roles: ['admin'],
            description: 'Master report card templates'
        },
        {
            name: 'Toddler Reports',
            path: '/ToddlerReports',
            roles: ['admin', 'toddlerteacher', 'toddlerassistant'],
            description: 'Daily reports for toddler program students'
        },
        {
            name: 'Accident Reports',
            path: '/AccidentReports',
            roles: ['admin', 'officemgr'],
            description: 'Incident reporting and tracking'
        },
        {
            name: 'Chat Queries',
            path: '/ChatQueries',
            roles: ['admin'],
            description: 'AI agent chat interactions'
        },
    ].filter(resource => hasAccess(resource.roles));

    const scrollToSection = (sectionId) => {
        // Close all sections and open only the clicked one
        setExpandedSection(sectionId);
        setActiveSection(sectionId);
        
        // Small delay to ensure accordion is expanded before scrolling
        setTimeout(() => {
            const element = document.getElementById(sectionId);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    };

    const handleAccordionChange = (sectionId) => (event, isExpanded) => {
        // If expanding, close all others and open this one
        // If collapsing, just close this one
        if (isExpanded) {
            setExpandedSection(sectionId);
            setActiveSection(sectionId);
        } else {
            setExpandedSection(null);
        }
    };

    const tocItems = [
        { id: 'introduction', label: 'Introduction' },
        { id: 'getting-started', label: 'Getting Started' },
        ...(hasAccess(['admin', 'teacher', 'toddlerteacher', 'officemgr', 'toddlerassistant']) ? [{ id: 'student-management', label: 'Student Management' }] : []),
        ...(hasAccess(['admin', 'officemgr']) ? [{ id: 'applications', label: 'Applications' }] : []),
        ...(hasAccess(['admin', 'officemgr']) ? [{ id: 'transitioning-student', label: 'Transitioning Student' }] : []),
        ...(hasAccess(['admin', 'officemgr']) ? [{ id: 'schedule-updates', label: 'Schedule Updates' }] : []),
        ...(hasAccess(['admin', 'officemgr']) ? [{ id: 'tour-management', label: 'Tour Management' }] : []),
        ...(hasAccess(['admin', 'officemgr']) ? [{ id: 'employees', label: 'Employees' }] : []),
        ...(hasAccess(['admin', 'officemgr']) ? [{ id: 'summaries', label: 'Financial Summaries' }] : []),
        ...(hasAccess(['admin', 'officemgr']) ? [{ id: 'student-attendance', label: 'Student Attendance' }] : []),
        ...(hasAccess(['admin', 'officemgr']) ? [{ id: 'student-absences', label: 'Student Absences' }] : []),
        ...(hasAccess(['admin', 'teacher', 'toddlerteacher', 'officemgr']) ? [{ id: 'report-cards', label: 'Report Cards' }] : []),
        ...(hasAccess(['admin', 'officemgr']) ? [{ id: 'accident-reports', label: 'Accident Reports' }] : []),
        ...(hasAccess(['admin']) ? [{ id: 'receipts', label: 'Receipts' }] : []),
        ...(hasAccess(['admin']) ? [{ id: 'payments', label: 'Payments' }] : []),
        ...(hasAccess(['admin']) ? [{ id: 'master-report-cards', label: 'Master Report Cards' }] : []),
        ...(hasAccess(['admin', 'toddlerteacher', 'toddlerassistant']) ? [{ id: 'toddler-reports', label: 'Toddler Reports' }] : []),
        ...(hasAccess(['admin']) ? [{ id: 'employee-hours', label: 'Employee Hours' }] : []),
        ...(hasAccess(['admin']) ? [{ id: 'chat-queries', label: 'Chat Queries' }] : []),
    ];

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                style: { width: DRAWER_WIDTH, maxWidth: '90vw' }
            }}
        >
            <Box style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <AppBar position="static" color="primary">
                    <Toolbar>
                        <HelpIcon style={{ marginRight: 12 }} />
                        <Typography variant="h6" style={{ flexGrow: 1 }}>
                            LAMS Admin Console User Guide
                        </Typography>
                        <IconButton
                            color="inherit"
                            onClick={() => setShowTOC(!showTOC)}
                            aria-label="toggle table of contents"
                            title="Table of Contents"
                        >
                            <ListIcon />
                        </IconButton>
                        <IconButton
                            color="inherit"
                            onClick={onClose}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Toolbar>
                </AppBar>
                
                {/* Main Content Area */}
                <Box style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                    {/* Table of Contents Sidebar */}
                    {showTOC && (
                        <Paper 
                            style={{ 
                                width: TOC_WIDTH, 
                                flexShrink: 0, 
                                overflow: 'auto',
                                borderRight: '1px solid #e0e0e0'
                            }}
                            elevation={0}
                        >
                            <Box p={2}>
                                <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: 8 }}>
                                    Table of Contents
                                </Typography>
                                <List dense>
                                    {tocItems.map((item) => (
                                        <ListItem
                                            key={item.id}
                                            button
                                            selected={activeSection === item.id}
                                            onClick={() => scrollToSection(item.id)}
                                            style={{
                                                backgroundColor: activeSection === item.id ? '#e3f2fd' : 'transparent',
                                                borderRadius: 4,
                                                marginBottom: 4,
                                            }}
                                        >
                                            <ListItemText 
                                                primary={item.label}
                                                primaryTypographyProps={{ variant: 'body2' }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        </Paper>
                    )}
                    
                    {/* Scrollable Content */}
                    <Box style={{ flex: 1, overflow: 'auto', padding: 16 }}>
                        {/* Introduction */}
                        <Box id="introduction" style={{ scrollMarginTop: 16 }}>
                        <Accordion 
                            expanded={expandedSection === 'introduction'}
                            onChange={handleAccordionChange('introduction')}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Introduction</Typography>
                            </AccordionSummary>
                        <AccordionDetails>
                            <Box>
                                <Typography variant="body1" paragraph>
                                    The LAMS Admin Console is a comprehensive web-based administrative interface for managing all aspects of Little Angels Montessori School operations. This guide provides step-by-step instructions for performing common administrative tasks.
                                </Typography>
                                
                                <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16 }}>
                                    Access Levels
                                </Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemText primary="Admin: Full access to all resources and administrative functions" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText primary="Office Manager: Access to student management, applications, tours, and financial operations" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText primary="Teacher: Access to student information, report cards, and attendance for assigned classrooms" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText primary="Toddler Teacher: Access to toddler-specific reports and student information" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText primary="Toddler Assistant: Access to toddler daily reports" />
                                    </ListItem>
                                </List>

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16 }}>
                                    Key Resources Available
                                </Typography>
                                <Typography variant="body2" component="div">
                                    {availableResources.length > 0 ? (
                                        <ul>
                                            {availableResources.map((resource) => (
                                                <li key={resource.path}>
                                                    <strong>
                                                        <Link 
                                                            component={RouterLink} 
                                                            to={resource.path} 
                                                            onClick={onClose} 
                                                            color="primary"
                                                        >
                                                            {resource.name}
                                                        </Link>
                                                    </strong>: {resource.description}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <Typography variant="body2" color="textSecondary">
                                            No resources available for your role.
                                        </Typography>
                                    )}
                                </Typography>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                    </Box>

                    {/* Getting Started */}
                    <Box id="getting-started" style={{ scrollMarginTop: 16 }}>
                        <Accordion 
                            expanded={expandedSection === 'getting-started'}
                            onChange={handleAccordionChange('getting-started')}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Getting Started</Typography>
                            </AccordionSummary>
                        <AccordionDetails>
                            <Box>
                                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                    Logging In
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ol>
                                        <li>Navigate to the LAMS Admin Console URL (typically admin.lamsaz.com)</li>
                                        <li>Click the <strong>Login</strong> button</li>
                                        <li>Enter your credentials (managed through Auth0)</li>
                                        <li>Upon successful authentication, you'll be redirected to the Dashboard</li>
                                    </ol>
                                </Typography>

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16 }}>
                                    Navigation
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ul>
                                        <li>Use the left sidebar menu to navigate between different resources</li>
                                        <li>The Dashboard provides an overview of key metrics and recent activity</li>
                                        <li>Each resource typically has <strong>List</strong>, <strong>Create</strong>, <strong>Edit</strong>, and <strong>Show</strong> views</li>
                                        <li>Use filters to narrow down results in list views</li>
                                    </ul>
                                </Typography>

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16 }}>
                                    Common Actions
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ul>
                                        <li><strong>Create</strong>: Add new records (e.g., new student, new application)</li>
                                        <li><strong>Edit</strong>: Modify existing records</li>
                                        <li><strong>List</strong>: View and filter all records</li>
                                        <li><strong>Show</strong>: View detailed information about a specific record</li>
                                        <li><strong>Export</strong>: Download data as CSV for external analysis</li>
                                        <li><strong>Bulk Actions</strong>: Perform actions on multiple selected records</li>
                                    </ul>
                                </Typography>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                    </Box>

                    {/* Student Management - For users with student access */}
                    {hasAccess(['admin', 'teacher', 'toddlerteacher', 'officemgr', 'toddlerassistant']) && (
                    <Box id="student-management" style={{ scrollMarginTop: 16 }}>
                        <Accordion 
                            expanded={expandedSection === 'student-management'}
                            onChange={handleAccordionChange('student-management')}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Student Management</Typography>
                            </AccordionSummary>
                        <AccordionDetails>
                            <Box>
                                <Typography variant="body1" paragraph>
                                    Student management encompasses creating new student records, updating existing information, communicating with parents, and generating academic reports.
                                </Typography>

                                {(role === 'admin' || role === 'officemgr') && (
                                    <>
                                        <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16 }}>
                                            Creating a New Student
                                        </Typography>
                                <Box style={{ backgroundColor: '#ffebee', padding: 16, marginTop: 8, marginBottom: 16, borderRadius: 4, border: '2px solid #f44336' }}>
                                    <Typography variant="body2" style={{ fontWeight: 'bold', color: '#c62828', marginBottom: 8 }}>
                                        ⚠️ IMPORTANT: Manual Student Creation Should Be Avoided
                                    </Typography>
                                    <Typography variant="body2" component="div" style={{ color: '#c62828' }}>
                                        <strong>Students are automatically created when an application is approved.</strong> The standard workflow is:
                                        <ol style={{ marginTop: 8, marginBottom: 8 }}>
                                            <li>Parent submits an application through the online portal</li>
                                            <li><strong>Admin reviews and approves the application</strong> (Office Managers have view-only access and cannot approve)</li>
                                            <li><strong>Student record is automatically created</strong> with all information from the application</li>
                                        </ol>
                                        <strong>Manual student creation should only be used in exceptional circumstances</strong> (e.g., legacy students, special cases where no application exists). For all new enrollments, use the application approval process instead.
                                    </Typography>
                                </Box>
                                <Typography variant="body2" component="div" style={{ fontStyle: 'italic', marginBottom: 16 }}>
                                    If you need to create a student manually (not recommended), follow the steps below:
                                </Typography>
                                <Typography variant="body2" component="div">
                                    To manually create a new student record (use only when necessary):
                                    <ol>
                                        <li>Navigate to <strong><Link component={RouterLink} to="/Students" onClick={onClose} color="primary">Students</Link></strong> in the left sidebar menu</li>
                                        <li>Click the <strong>Create</strong> button (usually a "+" icon or "Create" button)</li>
                                        <li>Fill in the student information form:
                                            <ul>
                                                <li><strong>Student Info Section</strong>:
                                                    <ul>
                                                        <li><strong>Notes</strong>: Optional internal notes about the student</li>
                                                        <li><strong>Student Name</strong>: Full name of the student (required, 2-45 characters)</li>
                                                        <li><strong>Sex</strong>: Select M or F (required)</li>
                                                        <li><strong>Date of Birth</strong>: Format MM/DD/YYYY (required)</li>
                                                        <li><strong>Date of Joining</strong>: Format MM/DD/YYYY (required)</li>
                                                        <li><strong>Classroom</strong>: Select from Classroom 01, 02, 03, or 04 (required)</li>
                                                        <li><strong>Program</strong>: Select from Primary or Toddler options with HalfDay, FullDay, or ExtendedDay (required)</li>
                                                        <li><strong>Discount</strong>: Select Sibling Discount, Employee Discount, or None</li>
                                                        <li><strong>Address</strong>: Student's address</li>
                                                        <li><strong>Immunization Status</strong>: Complete, Not Provided, or Exemption</li>
                                                        <li><strong>Use of Media Approval</strong>: Yes or No (required)</li>
                                                    </ul>
                                                </li>
                                                <li><strong>Parent Information Section</strong>:
                                                    <ul>
                                                        <li>Click the <strong>+</strong> button to add a parent</li>
                                                        <li><strong>Parent Name</strong>: Full name (required, 2-45 characters)</li>
                                                        <li><strong>Address</strong>: Parent's address</li>
                                                        <li><strong>Phone</strong>: Contact phone number</li>
                                                        <li><strong>Email</strong>: Valid email address (required)</li>
                                                        <li>You can add multiple parents by clicking the <strong>+</strong> button again</li>
                                                    </ul>
                                                </li>
                                            </ul>
                                        </li>
                                        <li>Click <strong>Save</strong> to create the student record</li>
                                    </ol>
                                </Typography>
                                <Box style={{ backgroundColor: '#f5f5f5', padding: 12, marginTop: 8, borderRadius: 4 }}>
                                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>Important Notes:</Typography>
                                    <Typography variant="body2">
                                        • The system automatically assigns a unique Student ID and generates a PIN if not provided<br/>
                                        • Classroom assignment should match the program (Primary → Classroom 01/02, Toddler → Classroom 03/04)<br/>
                                        • Sibling discounts are automatically calculated if siblings are linked<br/>
                                        • At least one parent email is required for communication<br/>
                                        • <strong>Remember: Use the application approval process for all new enrollments to ensure data consistency</strong>
                                    </Typography>
                                </Box>
                                    </>
                                )}

                                {(role === 'admin' || role === 'officemgr') && (
                                    <>
                                        <Divider style={{ margin: '16px 0' }} />

                                        <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                            Updating Student Information
                                        </Typography>
                                        <Typography variant="body2" component="div">
                                            To update an existing student record:
                                            <ol>
                                                <li>Navigate to <strong><Link component={RouterLink} to="/Students" onClick={onClose} color="primary">Students</Link></strong></li>
                                                <li>Use filters to find the student:
                                                    <ul>
                                                        <li>Filter by <strong>Enrollment Status</strong> (Active, Pending, etc.)</li>
                                                        <li>Filter by <strong>Program</strong> (Primary, Toddler, or All)</li>
                                                        <li>Filter by <strong>Classroom</strong> if known</li>
                                                    </ul>
                                                </li>
                                                <li>Click on the student's name or use the <strong>Edit</strong> button to open the student record</li>
                                                <li>Update any fields as needed:
                                                    <ul>
                                                        <li><strong>Basic Info</strong>: Name, dates, classroom, program, tuition, discount</li>
                                                        <li><strong>Enrollment Status</strong>: Change status (Active, Pending, Graduated, On Hold, Disenrolled, etc.)</li>
                                                        <li><strong>Parent Information</strong>: Update contact details, add or remove parents</li>
                                                        <li><strong>Siblings</strong>: Link or unlink sibling relationships</li>
                                                        <li><strong>Notes</strong>: Add or update internal notes</li>
                                                        <li><strong>Balance</strong>: Update account balance</li>
                                                    </ul>
                                                </li>
                                                <li>Click <strong>Save</strong> to update the record</li>
                                            </ol>
                                        </Typography>
                                    </>
                                )}

                                <Box style={{ backgroundColor: '#e3f2fd', padding: 12, marginTop: 8, borderRadius: 4 }}>
                                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>Access Levels:</Typography>
                                    <Typography variant="body2" component="div">
                                        <ul>
                                            <li><strong>Admin</strong>: Can edit all fields including enrollment status, program, classroom, and financial information</li>
                                            <li><strong>Office Manager</strong>: Can edit transitioning field and parent contact information</li>
                                            <li><strong>Teachers & Toddler Teachers</strong>: View-only access to student information in assigned classrooms</li>
                                            <li><strong>Toddler Assistant</strong>: View-only access to Active students in assigned toddler classrooms (Classroom 03 or 04)</li>
                                        </ul>
                                    </Typography>
                                </Box>

                                {(role === 'teacher' || role === 'toddlerteacher' || role === 'toddlerassistant') && (
                                    <>
                                        <Divider style={{ margin: '16px 0' }} />

                                        <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                            Teacher & Assistant Access
                                        </Typography>
                                        <Typography variant="body2" component="div">
                                            As a <strong>{role === 'toddlerassistant' ? 'Toddler Assistant' : role === 'toddlerteacher' ? 'Toddler Teacher' : 'Teacher'}</strong>, you have limited access to student information:
                                            <ul>
                                                <li><strong>View Only</strong>: You can view student information but cannot create or edit student records</li>
                                                <li><strong>Filtered View</strong>: You can only see Active students in your assigned classroom(s)</li>
                                                <li><strong>Available Actions</strong>:
                                                    <ul>
                                                        <li><strong>Generate Report Cards</strong>: Create report cards for Active students in your classroom
                                                            {role === 'toddlerassistant' && (
                                                                <span> (Note: You can generate report cards, but cannot access the Report Cards page to edit them. Contact a Teacher or Admin to edit report cards.)</span>
                                                            )}
                                                        </li>
                                                        {role === 'toddlerassistant' && (
                                                            <li><strong>Update Toddler Report Common Data</strong>: Update common data for Active students in toddler classrooms (Classroom 03 or 04)</li>
                                                        )}
                                                    </ul>
                                                </li>
                                            </ul>
                                        </Typography>
                                        <Box style={{ backgroundColor: '#fff3cd', padding: 12, marginTop: 8, borderRadius: 4 }}>
                                            <Typography variant="body2" style={{ fontWeight: 'bold' }}>Note:</Typography>
                                            <Typography variant="body2">
                                                {role === 'toddlerassistant' 
                                                    ? 'You can only view and work with Active students in your assigned toddler classroom. Contact an Admin or Office Manager to make changes to student records.'
                                                    : 'You can only view and work with Active students in your assigned classroom. Contact an Admin or Office Manager to make changes to student records.'}
                                            </Typography>
                                        </Box>
                                    </>
                                )}

                                {(role === 'admin' || role === 'officemgr') && (
                                    <>
                                        <Divider style={{ margin: '16px 0' }} />

                                        <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                            Creating Draft Emails for Students
                                        </Typography>
                                        <Typography variant="body2" component="div">
                                            To create a draft email to send to selected students' parents:
                                            <ol>
                                                <li>Navigate to <strong><Link component={RouterLink} to="/Students" onClick={onClose} color="primary">Students</Link></strong></li>
                                                <li>Filter to find the students you want to email (e.g., filter by Classroom, Enrollment Status)</li>
                                                <li>Select one or more students by checking the boxes next to their names</li>
                                                <li>Click the <strong>Draft Email</strong> button (email icon) in the toolbar</li>
                                                <li>The system will:
                                                    <ul>
                                                        <li>Create a draft email in your Gmail account</li>
                                                        <li>Include all parent email addresses from the selected students</li>
                                                        <li>Use a default subject and message template (you can edit these in Gmail)</li>
                                                    </ul>
                                                </li>
                                                <li>Open your Gmail account to review, edit, and send the draft email</li>
                                            </ol>
                                        </Typography>
                                        <Box style={{ backgroundColor: '#fff3cd', padding: 12, marginTop: 8, borderRadius: 4 }}>
                                            <Typography variant="body2" style={{ fontWeight: 'bold' }}>⚠️ Important:</Typography>
                                            <Typography variant="body2">
                                                • The draft email is created in the Gmail account associated with your login<br/>
                                                • You must review and edit the draft before sending<br/>
                                                • All parent emails from selected students will be included as recipients<br/>
                                                • Make sure to personalize the message before sending
                                            </Typography>
                                        </Box>
                                    </>
                                )}

                                {role === 'admin' && (
                                    <>
                                        <Divider style={{ margin: '16px 0' }} />

                                        <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                            Generating Report Cards (Admin Only)
                                        </Typography>
                                        <Typography variant="body2" component="div">
                                            To generate report cards for selected students:
                                            <ol>
                                                <li>Navigate to <strong><Link component={RouterLink} to="/Students" onClick={onClose} color="primary">Students</Link></strong></li>
                                                <li>Filter to find the students you want to generate report cards for:
                                                    <ul>
                                                        <li>Filter by <strong>Enrollment Status: "Active"</strong></li>
                                                        <li>Optionally filter by <strong>Classroom</strong> or <strong>Program</strong></li>
                                                    </ul>
                                                </li>
                                                <li>Select one or more students by checking the boxes next to their names</li>
                                                <li>Click the <strong>Generate Report Cards</strong> button (book icon) in the toolbar</li>
                                                <li>In the dialog that appears:
                                                    <ul>
                                                        <li>Select the <strong>ReportCard Year</strong> (current year, previous year, next year, or 2 years prior)</li>
                                                        <li>Select the <strong>ReportCard Semester</strong> (Spring or Fall)</li>
                                                        <li>Click <strong>Submit</strong> to generate the report cards</li>
                                                    </ul>
                                                </li>
                                                <li>The system will:
                                                    <ul>
                                                        <li>Create report card records for each selected student</li>
                                                        <li>Base the report card on the Master Report Card template for the student's program/classroom</li>
                                                        <li>Copy evaluations from the previous semester's report card (if available) for easy editing</li>
                                                        {role !== 'toddlerassistant' && (
                                                            <li>Navigate you to the <strong><Link component={RouterLink} to="/ReportCards" onClick={onClose} color="primary">Report Cards</Link></strong> page</li>
                                                        )}
                                                        {role === 'toddlerassistant' && (
                                                            <li>Report cards are created, but you cannot access the Report Cards page. Contact a Teacher or Admin to edit the generated report cards.</li>
                                                        )}
                                                    </ul>
                                                </li>
                                            </ol>
                                        </Typography>
                                        <Box style={{ backgroundColor: '#f5f5f5', padding: 12, marginTop: 8, borderRadius: 4 }}>
                                            <Typography variant="body2" style={{ fontWeight: 'bold' }}>Important Notes:</Typography>
                                            <Typography variant="body2">
                                                • Report cards are not generated if one already exists for the same student, semester, and year<br/>
                                                • The system automatically selects the appropriate Master Report Card template based on the student's program and classroom<br/>
                                                • For Primary students, previous semester evaluations are copied to save time<br/>
                                                • Toddler students do not have evaluations copied from previous report cards<br/>
                                                • After generation, teachers can edit the report cards to add evaluations and notes
                                            </Typography>
                                        </Box>
                                    </>
                                )}

                                {(role === 'teacher' || role === 'toddlerteacher') && (
                                    <>
                                        <Divider style={{ margin: '16px 0' }} />

                                        <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                            Editing Report Cards
                                        </Typography>
                                        <Typography variant="body2" component="div">
                                            After generating report cards, you can edit them:
                                            <ol>
                                                <li>Navigate to <strong><Link component={RouterLink} to="/ReportCards" onClick={onClose} color="primary">Report Cards</Link></strong></li>
                                                <li>Filter by Classroom, Semester, and Year to find the report cards you need to edit</li>
                                                <li>Click on a report card to open it for editing</li>
                                                <li>Fill in the evaluations for each category:
                                                    <ul>
                                                        <li>Update ratings/assessments for each category</li>
                                                        <li>Add notes about the student's progress</li>
                                                        <li>Review and modify any copied evaluations from previous semesters</li>
                                                    </ul>
                                                </li>
                                                <li>Click <strong>Save</strong> to save your changes</li>
                                                <li>Use the <strong>Send Report Card Email</strong> button to email completed report cards to parents</li>
                                            </ol>
                                        </Typography>
                                    </>
                                )}

                                <Divider style={{ margin: '16px 0' }} />

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                    Best Practices
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ul>
                                        <li><strong>Use Application Approval Process</strong>: Always approve applications to automatically create student records instead of manual creation</li>
                                        <li><strong>Complete Information</strong>: Always fill in all required fields when creating students (only when manual creation is necessary)</li>
                                        <li><strong>Accurate Dates</strong>: Ensure dates of birth and joining are correct for proper age calculations</li>
                                        <li><strong>Parent Contacts</strong>: Keep parent email addresses and phone numbers up to date</li>
                                        <li><strong>Regular Updates</strong>: Update student information as changes occur (address, contact info, program changes)</li>
                                        <li><strong>Batch Operations</strong>: Use bulk selection for generating report cards or sending emails to multiple students</li>
                                        <li><strong>Review Drafts</strong>: Always review draft emails before sending to ensure accuracy</li>
                                        <li><strong>Timely Report Cards</strong>: Generate report cards well in advance of deadlines to allow time for editing</li>
                                    </ul>
                                </Typography>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                    </Box>
                    )}

                    {/* Applications - Only for Admin and Office Manager */}
                    {hasAccess(['admin', 'officemgr']) && (
                    <Box id="applications" style={{ scrollMarginTop: 16 }}>
                        <Accordion 
                            expanded={expandedSection === 'applications'}
                            onChange={handleAccordionChange('applications')}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Applications</Typography>
                            </AccordionSummary>
                        <AccordionDetails>
                            <Box>
                                <Typography variant="body1" paragraph>
                                    Applications manage new student enrollment requests. Parents submit applications online, and administrators review and process them. <strong>Only Admins can approve applications</strong>; Office Managers have view-only access for reviewing application details.
                                </Typography>

                                {role === 'admin' && (
                                    <>
                                        <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16, color: '#1976d2' }}>
                                            Admin Role - Full Access
                                        </Typography>
                                        <Typography variant="body2" component="div">
                                            As an <strong>Admin</strong>, you have full access to all application features:
                                            <ul>
                                                <li><strong>View</strong>: See all applications with complete details</li>
                                                <li><strong>Create</strong>: Manually create new applications</li>
                                                <li><strong>Edit</strong>: Modify any application information</li>
                                                <li><strong>Delete</strong>: Remove applications (bulk delete available)</li>
                                                <li><strong>Send Emails</strong>: Send application-related emails to parents</li>
                                            </ul>
                                        </Typography>
                                    </>
                                )}

                                {role === 'officemgr' && (
                                    <>
                                        <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16, color: '#1976d2' }}>
                                            Office Manager Role - View and Review Access Only
                                        </Typography>
                                        <Typography variant="body2" component="div">
                                            As an <strong>Office Manager</strong>, you can:
                                            <ul>
                                                <li><strong>View</strong>: See all applications and their details</li>
                                                <li><strong>Review</strong>: Access full application information using the Show button</li>
                                            </ul>
                                            <Box style={{ backgroundColor: '#fff3cd', padding: 12, marginTop: 8, borderRadius: 4 }}>
                                                <Typography variant="body2" style={{ fontWeight: 'bold' }}>⚠️ Important:</Typography>
                                                <Typography variant="body2">
                                                    Office Managers have <strong>view-only access</strong>. You <strong>cannot approve, edit, or modify applications</strong>. To approve an application or make any changes, contact an Admin.
                                                </Typography>
                                            </Box>
                                        </Typography>
                                    </>
                                )}

                                <Divider style={{ margin: '16px 0' }} />

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                    Viewing Applications
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ol>
                                        <li>Navigate to <strong><Link component={RouterLink} to="/Applications" onClick={onClose} color="primary">Applications</Link></strong></li>
                                        <li>Use filters to find specific applications:
                                            <ul>
                                                <li><strong>Status</strong>: Submitted, WaitList, Approved, Enrolled, Rejected, Withdrawn, NeedMoreInfo, Alumni</li>
                                                <li><strong>Program Name</strong>: Toddler, Primary, or All</li>
                                            </ul>
                                        </li>
                                        <li>Click on an application or use <strong>Show</strong> to view full details</li>
                                    </ol>
                                </Typography>

                                {role === 'admin' && (
                                    <>
                                        <Divider style={{ margin: '16px 0' }} />

                                        <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                            Reviewing and Approving Applications (Admin Only)
                                        </Typography>
                                        <Typography variant="body2" component="div">
                                            <strong>Only Admins can approve applications.</strong> To review and approve an application:
                                            <ol>
                                                <li>Open an application using <strong>Show</strong> or <strong>Edit</strong></li>
                                                <li>Review all information across the tabs:
                                                    <ul>
                                                        <li><strong>Child's Information</strong>: Personal details, program selection</li>
                                                        <li><strong>Parent Information</strong>: Contact details for both parents</li>
                                                        <li><strong>Emergency Contacts</strong>: Emergency contact information</li>
                                                        <li><strong>Additional Information</strong>: Other relevant details</li>
                                                    </ul>
                                                </li>
                                                <li>Change the <strong>Status</strong> to <strong>"Approved"</strong></li>
                                                <li>When status is Approved:
                                                    <ul>
                                                        <li>Select <strong>Assigned Classroom</strong> based on program (Primary → Classroom 01/02, Toddler → Classroom 03/04)</li>
                                                        <li>Set <strong>Approved enrollment Date</strong> if different from desired date (leave blank to use desired date)</li>
                                                    </ul>
                                                </li>
                                                <li>Click <strong>Save</strong> to update the application</li>
                                            </ol>
                                        </Typography>
                                        <Box style={{ backgroundColor: '#e3f2fd', padding: 12, marginTop: 8, borderRadius: 4 }}>
                                            <Typography variant="body2" style={{ fontWeight: 'bold' }}>Application Statuses:</Typography>
                                            <Typography variant="body2" component="div">
                                                <ul>
                                                    <li><strong>Submitted</strong>: New application received</li>
                                                    <li><strong>Approved</strong>: Application approved, ready for enrollment</li>
                                                    <li><strong>Enrolled</strong>: Student has been enrolled</li>
                                                    <li><strong>WaitList</strong>: Added to waiting list</li>
                                                    <li><strong>Rejected</strong>: Application rejected</li>
                                                    <li><strong>Withdrawn</strong>: Parent withdrew the application</li>
                                                    <li><strong>NeedMoreInfo</strong>: Additional information required</li>
                                                    <li><strong>Alumni</strong>: Former student</li>
                                                </ul>
                                            </Typography>
                                        </Box>

                                        <Divider style={{ margin: '16px 0' }} />

                                        <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                            Sending Application Emails
                                        </Typography>
                                        <Typography variant="body2" component="div">
                                            To send emails to applicants:
                                            <ol>
                                                <li>Select one or more applications by checking the boxes</li>
                                                <li>Click the <strong>Send Application Email</strong> button (email icon) in the toolbar</li>
                                                <li>The system will generate and send emails to the selected applicants</li>
                                            </ol>
                                        </Typography>

                                        <Divider style={{ margin: '16px 0' }} />

                                        <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                            Creating Applications Manually
                                        </Typography>
                                        <Typography variant="body2" component="div">
                                            To create a new application manually:
                                            <ol>
                                                <li>Click <strong>Create</strong> in the Applications list</li>
                                                <li>Fill in all required information across the tabs</li>
                                                <li>Set initial status (typically "Submitted")</li>
                                                <li>Click <strong>Save</strong> to create the application</li>
                                            </ol>
                                        </Typography>
                                    </>
                                )}

                                <Divider style={{ margin: '16px 0' }} />

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                    Best Practices
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ul>
                                        <li><strong>Review Promptly</strong>: Review and respond to applications in a timely manner</li>
                                        <li><strong>Complete Information</strong>: Ensure all required fields are filled before approving</li>
                                        <li><strong>Classroom Assignment</strong>: Assign appropriate classroom based on program and availability</li>
                                        <li><strong>Status Updates</strong>: Keep application status current to reflect the enrollment process</li>
                                        {role === 'admin' && (
                                            <>
                                                <li><strong>Communication</strong>: Use email functionality to keep parents informed</li>
                                                <li><strong>Data Accuracy</strong>: Verify all information before approving applications</li>
                                            </>
                                        )}
                                    </ul>
                                </Typography>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                    </Box>
                    )}

                    {/* Transitioning Student - Only for Admin and Office Manager */}
                    {hasAccess(['admin', 'officemgr']) && (
                    <Box id="transitioning-student" style={{ scrollMarginTop: 16 }}>
                        <Accordion 
                            expanded={expandedSection === 'transitioning-student'}
                            onChange={handleAccordionChange('transitioning-student')}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Transitioning a Student from Toddler to Primary</Typography>
                            </AccordionSummary>
                        <AccordionDetails>
                            <Box>
                                <Typography variant="body1" paragraph>
                                    Transitioning a student from the Toddler program to the Primary program is a multi-step process that involves setting up the transition and scheduling it for a specific date. This process ensures proper classroom assignment, program change, and fee adjustments.
                                </Typography>

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16 }}>
                                    Prerequisites
                                </Typography>
                                <Typography variant="body2" component="div">
                                    Before you can transition a student, ensure:
                                    <ol>
                                        <li>The student is currently enrolled in a <strong>Toddler</strong> program:
                                            <ul>
                                                <li>Program must be one of: <code>Toddler - HalfDay</code>, <code>Toddler - FullDay</code>, or <code>Toddler - ExtendedDay</code></li>
                                                <li>Student must be in <strong>Classroom 03</strong> or <strong>Classroom 04</strong> (toddler classrooms)</li>
                                            </ul>
                                        </li>
                                        <li>The student's enrollment status is <strong>Active</strong></li>
                                        <li>You have <strong>Admin</strong> or <strong>Office Manager</strong> role permissions</li>
                                    </ol>
                                </Typography>

                                <Divider style={{ margin: '16px 0' }} />

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                    Step 1: Locate the Student
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ol>
                                        <li>Navigate to <strong><Link component={RouterLink} to="/Students" onClick={onClose} color="primary">Students</Link></strong> in the left sidebar menu</li>
                                        <li>Use the filters to find the student:
                                            <ul>
                                                <li>Set <strong>Enrollment Status</strong> filter to "Active"</li>
                                                <li>Set <strong>Program</strong> filter to "Toddler" (or "All" if needed)</li>
                                                <li>Set <strong>Classroom</strong> filter if you know the student's classroom</li>
                                            </ul>
                                        </li>
                                        <li>Click on the student's name or use the <strong>Edit</strong> button to open the student record</li>
                                    </ol>
                                </Typography>

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16 }}>
                                    Step 2: Set the Transitioning Classroom
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ol>
                                        <li>In the student edit form, locate the <strong>Transitioning</strong> field</li>
                                        <li>Select the target primary classroom:
                                            <ul>
                                                <li><strong>Classroom 01</strong> - Primary classroom option 1</li>
                                                <li><strong>Classroom 02</strong> - Primary classroom option 2</li>
                                                <li><strong>Blank</strong> - Clear any existing transition setting</li>
                                            </ul>
                                        </li>
                                        <li>Click <strong>Save</strong> to update the student record</li>
                                    </ol>
                                </Typography>
                                <Box style={{ backgroundColor: '#f5f5f5', padding: 12, marginTop: 8, borderRadius: 4 }}>
                                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>Important Notes:</Typography>
                                    <Typography variant="body2">
                                        • The <strong>Transitioning</strong> field indicates which primary classroom the student will move to<br/>
                                        • This field must be set before creating a Schedule Update<br/>
                                        • The student will remain in their current toddler classroom until the transition date
                                    </Typography>
                                </Box>

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16 }}>
                                    Step 3: Create a Schedule Update
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ol>
                                        <li>Navigate to <strong><Link component={RouterLink} to="/ScheduleUpdates" onClick={onClose} color="primary">Schedule Updates</Link></strong> in the left sidebar menu</li>
                                        <li>Click the <strong>Create</strong> button (usually a "+" icon or "Create" button)</li>
                                        <li>Fill in the Schedule Update form:
                                            <ul>
                                                <li><strong>Student Selection</strong>: In the <strong>Student ID</strong> field, select the student you want to transition</li>
                                                <li><strong>Update Type</strong>: Select <strong>Transition</strong> from the <strong>UpdateType</strong> dropdown</li>
                                                <li><strong>Effective Date</strong>: Enter the effective date in <strong>MM/DD/YYYY</strong> format (e.g., <code>03/01/2025</code>)</li>
                                            </ul>
                                        </li>
                                        <li>Click <strong>Save</strong> to create the schedule update</li>
                                    </ol>
                                </Typography>
                                <Box style={{ backgroundColor: '#fff3cd', padding: 12, marginTop: 8, borderRadius: 4 }}>
                                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>⚠️ Critical:</Typography>
                                    <Typography variant="body2">
                                        The effective date <strong>must be the 1st of a month</strong>. The system validates that the date is the beginning of a month.
                                    </Typography>
                                </Box>

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16 }}>
                                    Step 4: Verify the Schedule Update
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ol>
                                        <li>Return to the <strong><Link component={RouterLink} to="/ScheduleUpdates" onClick={onClose} color="primary">Schedule Updates</Link></strong> list view</li>
                                        <li>Filter by:
                                            <ul>
                                                <li><strong>Update Type</strong>: "Transition"</li>
                                                <li><strong>Status</strong>: "Scheduled"</li>
                                            </ul>
                                        </li>
                                        <li>Locate your newly created schedule update</li>
                                        <li>Verify:
                                            <ul>
                                                <li>Student name is correct</li>
                                                <li>Effective date is correct (1st of month)</li>
                                                <li>Status shows "Scheduled"</li>
                                            </ul>
                                        </li>
                                    </ol>
                                </Typography>

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16 }}>
                                    Step 5: Monitor Transition Processing
                                </Typography>
                                <Typography variant="body2" paragraph>
                                    The transition is automatically processed by a scheduled job (cron) that runs daily. On the effective date (or the day before, depending on the cron schedule), the system will:
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ol>
                                        <li><strong>Change the Program</strong>:
                                            <ul>
                                                <li>Convert from <code>Toddler - HalfDay</code> → <code>Primary - HalfDay</code></li>
                                                <li>Convert from <code>Toddler - FullDay</code> → <code>Primary - FullDay</code></li>
                                                <li>Convert from <code>Toddler - ExtendedDay</code> → <code>Primary - ExtendedDay</code></li>
                                            </ul>
                                        </li>
                                        <li><strong>Change the Classroom</strong>: Move student from their current toddler classroom (03 or 04) to the classroom specified in the <strong>Transitioning</strong> field (01 or 02)</li>
                                        <li><strong>Clear the Transitioning Field</strong>: The <strong>Transitioning</strong> field is cleared after successful transition</li>
                                        <li><strong>Update Schedule Update Status</strong>: Status changes from "Scheduled" to "Completed"</li>
                                    </ol>
                                </Typography>

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16 }}>
                                    Step 6: Verify Completion
                                </Typography>
                                <Typography variant="body2" component="div">
                                    After the transition date has passed:
                                    <ol>
                                        <li>Navigate to <strong><Link component={RouterLink} to="/ScheduleUpdates" onClick={onClose} color="primary">Schedule Updates</Link></strong></li>
                                        <li>Filter by the student and check the status:
                                            <ul>
                                                <li><strong>Completed</strong>: Transition was successful</li>
                                                <li><strong>Failed</strong>: Check the Error field for details</li>
                                                <li><strong>Scheduled</strong>: Transition hasn't been processed yet</li>
                                            </ul>
                                        </li>
                                        <li>Verify the student record:
                                            <ul>
                                                <li>Navigate to <strong><Link component={RouterLink} to="/Students" onClick={onClose} color="primary">Students</Link></strong> and open the student record</li>
                                                <li>Check that <strong>Program</strong> now shows a Primary program</li>
                                                <li>Check that <strong>Classroom</strong> shows the new primary classroom (01 or 02)</li>
                                                <li>Check that <strong>Transitioning</strong> field is blank</li>
                                            </ul>
                                        </li>
                                    </ol>
                                </Typography>

                                <Divider style={{ margin: '16px 0' }} />

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                    Troubleshooting
                                </Typography>
                                
                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                                            Error: "Student not Toddler or not transitioning"
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box>
                                            <Typography variant="body2" style={{ fontWeight: 'bold' }}>Cause:</Typography>
                                            <Typography variant="body2">The student is either not in a Toddler program, or the <strong>Transitioning</strong> field is not set.</Typography>
                                            <Typography variant="body2" style={{ fontWeight: 'bold', marginTop: 8 }}>Solution:</Typography>
                                            <Typography variant="body2" component="div">
                                                <ol>
                                                    <li>Verify the student's current program includes "Toddler"</li>
                                                    <li>Go to the student edit form and set the <strong>Transitioning</strong> field</li>
                                                    <li>Save the student record</li>
                                                    <li>Try creating the Schedule Update again</li>
                                                </ol>
                                            </Typography>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                                            Error: "Transition effective date has to be the 1st of the month"
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box>
                                            <Typography variant="body2" style={{ fontWeight: 'bold' }}>Cause:</Typography>
                                            <Typography variant="body2">The effective date you entered is not the first day of a month.</Typography>
                                            <Typography variant="body2" style={{ fontWeight: 'bold', marginTop: 8 }}>Solution:</Typography>
                                            <Typography variant="body2">
                                                Change the effective date to the 1st of your desired month (e.g., <code>03/01/2025</code> instead of <code>03/15/2025</code>).
                                            </Typography>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                                            Transition Status Shows "Failed"
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box>
                                            <Typography variant="body2" style={{ fontWeight: 'bold' }}>Cause:</Typography>
                                            <Typography variant="body2">The automated transition process encountered an error.</Typography>
                                            <Typography variant="body2" style={{ fontWeight: 'bold', marginTop: 8 }}>Solution:</Typography>
                                            <Typography variant="body2" component="div">
                                                <ol>
                                                    <li>Check the <strong>Error</strong> field in the Schedule Update record for details</li>
                                                    <li>Verify the student record still exists and is Active</li>
                                                    <li>Check that the transitioning classroom is valid (01 or 02)</li>
                                                    <li>If needed, manually update the student record</li>
                                                </ol>
                                            </Typography>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>

                                <Divider style={{ margin: '16px 0' }} />

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                    Best Practices
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ul>
                                        <li><strong>Plan Ahead</strong>: Create schedule updates well in advance of the transition date</li>
                                        <li><strong>Verify Prerequisites</strong>: Always check that the student is in a Toddler program and has the Transitioning field set</li>
                                        <li><strong>Use First of Month</strong>: Always use the 1st of a month as the effective date</li>
                                        <li><strong>Monitor Status</strong>: Regularly check Schedule Updates to ensure transitions are processing correctly</li>
                                        <li><strong>Document Changes</strong>: Add notes to the student record if there are special circumstances</li>
                                    </ul>
                                </Typography>
                            </Box>
                        </AccordionDetails>
                        </Accordion>
                        </Box>
                    )}

                    {/* Schedule Updates - Only for Admin and Office Manager */}
                    {hasAccess(['admin', 'officemgr']) && (
                    <Box id="schedule-updates" style={{ scrollMarginTop: 16 }}>
                        <Accordion 
                            expanded={expandedSection === 'schedule-updates'}
                            onChange={handleAccordionChange('schedule-updates')}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Schedule Updates</Typography>
                            </AccordionSummary>
                        <AccordionDetails>
                            <Box>
                                <Typography variant="body1" paragraph>
                                    Schedule Updates allow you to schedule future changes to student records. Changes are automatically processed on the effective date.
                                </Typography>

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16 }}>
                                    Creating a Schedule Update
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ol>
                                        <li>Navigate to <strong><Link component={RouterLink} to="/ScheduleUpdates" onClick={onClose} color="primary">Schedule Updates</Link></strong></li>
                                        <li>Click <strong>Create</strong> and select:
                                            <ul>
                                                <li><strong>Student</strong>: Choose the student</li>
                                                <li><strong>Update Type</strong>: Select the type of change</li>
                                                <li><strong>Effective Date</strong>: When the change should take effect</li>
                                                <li>Additional fields based on the update type</li>
                                            </ul>
                                        </li>
                                        <li>Click <strong>Save</strong> to schedule the update</li>
                                    </ol>
                                </Typography>

                                <Divider style={{ margin: '16px 0' }} />

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                    Update Types
                                </Typography>

                                <Typography variant="body2" style={{ fontWeight: 'bold', marginTop: 12 }}>
                                    Disenroll
                                </Typography>
                                <Typography variant="body2" component="div">
                                    Removes a student from the school.
                                    <ul>
                                        <li><strong>When</strong>: Student must be Active</li>
                                        <li><strong>Effective Date</strong>: Last day the child attends school</li>
                                        <li><strong>Result</strong>: Sets graduation date and changes status</li>
                                    </ul>
                                </Typography>

                                <Typography variant="body2" style={{ fontWeight: 'bold', marginTop: 12 }}>
                                    Graduate
                                </Typography>
                                <Typography variant="body2" component="div">
                                    Marks a student as graduated.
                                    <ul>
                                        <li><strong>When</strong>: Student must be Active</li>
                                        <li><strong>Effective Date</strong>: Graduation date</li>
                                        <li><strong>Result</strong>: Sets graduation date</li>
                                    </ul>
                                </Typography>

                                <Typography variant="body2" style={{ fontWeight: 'bold', marginTop: 12 }}>
                                    Transition
                                </Typography>
                                <Typography variant="body2" component="div">
                                    Moves a student from Toddler to Primary program.
                                    <ul>
                                        <li><strong>When</strong>: Student must be Active, in Toddler program, with Transitioning field set</li>
                                        <li><strong>Effective Date</strong>: Must be the 1st of a month</li>
                                        <li><strong>Result</strong>: Changes program and classroom automatically</li>
                                        <li>See the <strong>Transitioning Student</strong> section above for detailed step-by-step instructions</li>
                                    </ul>
                                </Typography>

                                <Typography variant="body2" style={{ fontWeight: 'bold', marginTop: 12 }}>
                                    Change Program
                                </Typography>
                                <Typography variant="body2" component="div">
                                    Changes program type (HalfDay/FullDay/ExtendedDay) within the same age group (Primary or Toddler).
                                    <ul>
                                        <li><strong>Program Options</strong>: Select HalfDay, FullDay, or ExtendedDay (must be different from current program)</li>
                                        <li><strong>Result</strong>: Updates program on the effective date and automatically generates an invoice if tuition increases</li>
                                    </ul>
                                </Typography>

                                <Box style={{ backgroundColor: '#e3f2fd', padding: 12, marginTop: 12, borderRadius: 4 }}>
                                    <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: 8 }}>For Active Students:</Typography>
                                    <Typography variant="body2" component="div">
                                        <ul style={{ marginTop: 0 }}>
                                            <li><strong>6-Month Rule</strong>: Student must have been enrolled for at least 6 months, and it must be at least 6 months since their last program change (if any)</li>
                                            <li><strong>Effective Date Options</strong>:
                                                <ul>
                                                    <li><strong>Increasing Tuition</strong> (e.g., HalfDay → FullDay): Can be any date within the current month or in the future</li>
                                                    <li><strong>Decreasing Tuition</strong> (e.g., FullDay → HalfDay): Must be the 1st of a future month</li>
                                                </ul>
                                            </li>
                                            <li><strong>What Happens</strong>: Program changes on the effective date. If tuition increases, a new invoice is automatically generated for the difference</li>
                                        </ul>
                                    </Typography>
                                </Box>

                                <Box style={{ backgroundColor: '#f5f5f5', padding: 12, marginTop: 12, borderRadius: 4 }}>
                                    <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: 8 }}>For Pending Students:</Typography>
                                    <Typography variant="body2" component="div">
                                        <ul style={{ marginTop: 0 }}>
                                            <li><strong>No Restrictions</strong>: Can change program with any effective date</li>
                                            <li><strong>Effective Date</strong>: Can be any date (typically the student's joining date or a future date)</li>
                                        </ul>
                                    </Typography>
                                </Box>

                                <Box style={{ backgroundColor: '#fff3cd', padding: 12, marginTop: 12, borderRadius: 4 }}>
                                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>💡 Communicating with Parents:</Typography>
                                    <Typography variant="body2" component="div" style={{ marginTop: 8 }}>
                                        When discussing program changes with parents:
                                        <ul style={{ marginTop: 8 }}>
                                            <li><strong>For Increases</strong>: "The program change will take effect on [date]. A new invoice will be generated for the tuition difference."</li>
                                            <li><strong>For Decreases</strong>: "The program change will take effect on the 1st of [month]. The reduced tuition will apply starting that date."</li>
                                            <li><strong>6-Month Rule</strong>: "Program changes are allowed every 6 months to ensure stability. Your child has been enrolled for [X] months, so this change is eligible."</li>
                                        </ul>
                                    </Typography>
                                </Box>

                                <Typography variant="body2" style={{ fontWeight: 'bold', marginTop: 12 }}>
                                    Change Joining Date
                                </Typography>
                                <Typography variant="body2" component="div">
                                    Updates the enrollment date for a pending student.
                                    <ul>
                                        <li><strong>When</strong>: Student must be Pending status</li>
                                        <li><strong>Effective Date</strong>: New joining date</li>
                                        <li><strong>Result</strong>: Updates joining date immediately</li>
                                    </ul>
                                </Typography>

                                <Typography variant="body2" style={{ fontWeight: 'bold', marginTop: 12 }}>
                                    Change Media Preference
                                </Typography>
                                <Typography variant="body2" component="div">
                                    Updates the "Use of Media Approval" setting.
                                    <ul>
                                        <li><strong>When</strong>: Any time</li>
                                        <li><strong>Use of Media</strong>: Select Y (Yes) or N (No)</li>
                                        <li><strong>Result</strong>: Updates immediately, no effective date needed</li>
                                    </ul>
                                </Typography>

                                <Divider style={{ margin: '16px 0' }} />

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                    Monitoring Schedule Updates
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ul>
                                        <li><strong>Scheduled</strong>: Waiting to be processed on the effective date</li>
                                        <li><strong>Completed</strong>: Successfully processed</li>
                                        <li><strong>Failed</strong>: Error occurred - check the Error field for details</li>
                                    </ul>
                                    <Typography variant="body2" style={{ marginTop: 8 }}>
                                        Only Scheduled updates can be deleted. Completed or Failed updates remain for record-keeping.
                                    </Typography>
                                </Typography>

                                <Divider style={{ margin: '16px 0' }} />

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                    Quick Tips
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ul>
                                        <li>Schedule updates well in advance for smooth processing</li>
                                        <li>Use 1st of month for transitions and program changes to lower tuition</li>
                                        <li>Check Failed updates regularly and resolve issues</li>
                                        <li>Program changes generate invoices automatically if tuition increases</li>
                                        <li>Media preference changes take effect immediately</li>
                                    </ul>
                                </Typography>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                    </Box>
                    )}

                    {/* Tour Management - Only for Admin and Office Manager */}
                    {hasAccess(['admin', 'officemgr']) && (
                    <Box id="tour-management" style={{ scrollMarginTop: 16 }}>
                        <Accordion 
                            expanded={expandedSection === 'tour-management'}
                            onChange={handleAccordionChange('tour-management')}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Tour Management</Typography>
                            </AccordionSummary>
                        <AccordionDetails>
                            <Box>
                                <Typography variant="body1" paragraph>
                                    Tour management allows you to schedule school tours for prospective families, approve tour requests, and block dates when tours are not available.
                                </Typography>

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16 }}>
                                    Creating a Tour
                                </Typography>
                                <Typography variant="body2" component="div">
                                    To create a new tour:
                                    <ol>
                                        <li>Navigate to <strong><Link component={RouterLink} to="/tours/TourDetails" onClick={onClose} color="primary">Tours</Link></strong> in the left sidebar menu</li>
                                        <li>Click the <strong>Create</strong> button (usually a "+" icon or "Create" button)</li>
                                        <li>Fill in the tour form:
                                            <ul>
                                                <li><strong>Available Dates & Time</strong>: Select from the dropdown of available tour slots, or choose "Enter custom value" to specify a custom date/time</li>
                                                <li><strong>Parent Name</strong>: Enter the parent's full name</li>
                                                <li><strong>Student Name</strong>: Enter the prospective student's name</li>
                                                <li><strong>Program Applying For</strong>: Select either "Toddler (18 - 36 months)" or "Primary (3 - 6 years)"</li>
                                                <li><strong>Enrollment Date</strong>: Enter the desired enrollment date (format: YYYY-MM-DD)</li>
                                                <li><strong>Email</strong>: Enter the parent's email address</li>
                                                <li><strong>Phone</strong>: Enter the parent's phone number</li>
                                            </ul>
                                        </li>
                                        <li>Click <strong>Save</strong> to create the tour</li>
                                    </ol>
                                </Typography>
                                <Box style={{ backgroundColor: '#f5f5f5', padding: 12, marginTop: 8, borderRadius: 4 }}>
                                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>Important Notes:</Typography>
                                    <Typography variant="body2">
                                        • New tours are created with status <strong>"Submitted"</strong><br/>
                                        • The system prevents duplicate tours for the same email address if there's already a Submitted or Approved tour<br/>
                                        • Tour dates are typically scheduled on Wednesdays, but custom dates can be entered<br/>
                                        • After creating a tour, you can approve it to confirm the appointment
                                    </Typography>
                                </Box>

                                <Divider style={{ margin: '16px 0' }} />

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                    Approving a Tour
                                </Typography>
                                <Typography variant="body2" component="div">
                                    To approve a tour request:
                                    <ol>
                                        <li>Navigate to <strong><Link component={RouterLink} to="/tours/TourDetails" onClick={onClose} color="primary">Tours</Link></strong></li>
                                        <li>Filter by <strong>Status: "Submitted"</strong> to see pending tour requests</li>
                                        <li>Click on the tour record you want to approve (or use the <strong>Edit</strong> button)</li>
                                        <li>In the tour edit form:
                                            <ul>
                                                <li>Change the <strong>Status</strong> from "Submitted" to <strong>"Approved"</strong></li>
                                                <li>Optionally check the <strong>"Send Email"</strong> checkbox to send a confirmation email to the parent</li>
                                                <li>Review and update any other tour details if needed</li>
                                            </ul>
                                        </li>
                                        <li>Click <strong>Save</strong> to approve the tour</li>
                                    </ol>
                                </Typography>
                                <Box style={{ backgroundColor: '#e3f2fd', padding: 12, marginTop: 8, borderRadius: 4 }}>
                                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>Status Options:</Typography>
                                    <Typography variant="body2" component="div">
                                        From "Submitted" status, you can change to:
                                        <ul>
                                            <li><strong>Approved</strong> - Confirms the tour appointment</li>
                                            <li><strong>Cancelled</strong> - Cancels the tour request</li>
                                        </ul>
                                        From "Approved" status, you can change to:
                                        <ul>
                                            <li><strong>Cancelled</strong> - Cancels the approved tour</li>
                                            <li><strong>Submitted</strong> - Reverts to submitted status</li>
                                            <li><strong>No Show</strong> - Marks if the family didn't attend</li>
                                            <li><strong>Completed</strong> - Marks the tour as completed</li>
                                        </ul>
                                    </Typography>
                                </Box>

                                <Divider style={{ margin: '16px 0' }} />

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                    Blocking Tour Dates
                                </Typography>
                                <Typography variant="body2" component="div">
                                    To block specific tour dates/times when tours are not available:
                                    <ol>
                                        <li>Navigate to <strong><Link component={RouterLink} to="/tours/TourDetails" onClick={onClose} color="primary">Tours</Link></strong></li>
                                        <li>Set the filter to <strong>Status: "Available"</strong> to see available tour slots</li>
                                        <li>Select one or more available tour slots by checking the boxes next to them</li>
                                        <li>Click the <strong>Block Tour Dates</strong> button (red block icon) in the toolbar</li>
                                        <li>In the dialog that appears:
                                            <ul>
                                                <li>Enter a <strong>Reason for Blocking</strong> (e.g., "Holiday", "School closed", "Staff training")</li>
                                                <li>Click <strong>Submit</strong> to block the selected dates</li>
                                            </ul>
                                        </li>
                                    </ol>
                                </Typography>
                                <Box style={{ backgroundColor: '#fff3cd', padding: 12, marginTop: 8, borderRadius: 4 }}>
                                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>⚠️ Important:</Typography>
                                    <Typography variant="body2">
                                        • Blocked dates will no longer appear as available options when creating new tours<br/>
                                        • You can unblock dates by selecting blocked tours and deleting them (using the bulk delete button)<br/>
                                        • Always provide a clear reason for blocking to help other staff understand why dates are unavailable
                                    </Typography>
                                </Box>

                                <Divider style={{ margin: '16px 0' }} />

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                    Additional Tour Actions
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ul>
                                        <li><strong>Rescheduling Tours</strong>: For tours with "Submitted" or "Approved" status, you can use the Reschedule button to change the date/time</li>
                                        <li><strong>Sending Tour Emails</strong>: Use the "Create Tours Email" button to send reminder emails to families with upcoming tours</li>
                                        <li><strong>Viewing Tour History</strong>: Filter by different statuses (Completed, Cancelled, No Show) to view tour history</li>
                                    </ul>
                                </Typography>

                                <Divider style={{ margin: '16px 0' }} />

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                    Best Practices
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ul>
                                        <li><strong>Approve Promptly</strong>: Review and approve tour requests in a timely manner to confirm appointments</li>
                                        <li><strong>Block in Advance</strong>: Block dates for holidays, school closures, or unavailable times well in advance</li>
                                        <li><strong>Use Clear Reasons</strong>: Always provide descriptive reasons when blocking dates</li>
                                        <li><strong>Follow Up</strong>: Mark tours as "Completed" after they occur, or "No Show" if the family doesn't attend</li>
                                        <li><strong>Send Reminders</strong>: Use the email functionality to send reminders before scheduled tours</li>
                                    </ul>
                                </Typography>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                    </Box>
                    )}

                    {/* Employees - Only for Admin and Office Manager */}
                    {hasAccess(['admin', 'officemgr']) && (
                    <Box id="employees" style={{ scrollMarginTop: 16 }}>
                        <Accordion 
                            expanded={expandedSection === 'employees'}
                            onChange={handleAccordionChange('employees')}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Employees</Typography>
                            </AccordionSummary>
                        <AccordionDetails>
                            <Box>
                                <Typography variant="body1" paragraph>
                                    View employee contact information and basic details.
                                </Typography>

                                {role === 'officemgr' && (
                                    <Box style={{ backgroundColor: '#fff3cd', padding: 12, marginTop: 8, borderRadius: 4 }}>
                                        <Typography variant="body2" style={{ fontWeight: 'bold' }}>Office Manager Access:</Typography>
                                        <Typography variant="body2">
                                            You can view employee names, email addresses, and phone numbers. Contact an Admin for detailed employee information or to make changes.
                                        </Typography>
                                    </Box>
                                )}

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16 }}>
                                    Viewing Employees
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ol>
                                        <li>Navigate to <strong><Link component={RouterLink} to="/Employees" onClick={onClose} color="primary">Employees</Link></strong></li>
                                        <li>Use filters to find employees by status or classroom</li>
                                        <li>View contact information for quick reference</li>
                                    </ol>
                                </Typography>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                    </Box>
                    )}

                    {/* Financial Summaries - Only for Admin and Office Manager */}
                    {hasAccess(['admin', 'officemgr']) && (
                    <Box id="summaries" style={{ scrollMarginTop: 16 }}>
                        <Accordion 
                            expanded={expandedSection === 'summaries'}
                            onChange={handleAccordionChange('summaries')}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Financial Summaries</Typography>
                            </AccordionSummary>
                        <AccordionDetails>
                            <Box>
                                {role === 'admin' && (
                                    <Typography variant="body1" paragraph>
                                        View financial summaries and reports for tracking school finances, including tuition totals and enrollment statistics.
                                    </Typography>
                                )}

                                {role === 'officemgr' && (
                                    <Typography variant="body1" paragraph>
                                        View enrollment summaries and student statistics by classroom and enrollment status.
                                    </Typography>
                                )}

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16 }}>
                                    Viewing Summaries
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ol>
                                        <li>Navigate to <strong><Link component={RouterLink} to="/Summaries" onClick={onClose} color="primary">Summaries</Link></strong></li>
                                        <li>Review available data and reports</li>
                                        <li>Use filters to find specific time periods or categories</li>
                                    </ol>
                                </Typography>
                                <Box style={{ backgroundColor: '#f5f5f5', padding: 12, marginTop: 8, borderRadius: 4 }}>
                                    {role === 'admin' ? (
                                        <Typography variant="body2">
                                            Summaries provide comprehensive financial information including tuition totals and enrollment statistics.
                                        </Typography>
                                    ) : (
                                        <Typography variant="body2">
                                            Summaries provide enrollment statistics and student counts by classroom. Contact an Admin for detailed financial information.
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                    </Box>
                    )}

                    {/* Student Attendance - Only for Admin and Office Manager */}
                    {hasAccess(['admin', 'officemgr']) && (
                    <Box id="student-attendance" style={{ scrollMarginTop: 16 }}>
                        <Accordion 
                            expanded={expandedSection === 'student-attendance'}
                            onChange={handleAccordionChange('student-attendance')}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Student Attendance</Typography>
                            </AccordionSummary>
                        <AccordionDetails>
                            <Box>
                                <Typography variant="body1" paragraph>
                                    Track student sign-in and sign-out times for daily attendance monitoring.
                                </Typography>

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16 }}>
                                    Recording Student Attendance
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ol>
                                        <li>Navigate to <strong><Link component={RouterLink} to="/StudentSignInOut" onClick={onClose} color="primary">Student Attendance</Link></strong></li>
                                        <li>View existing attendance records or create new entries</li>
                                        <li>Edit records to correct sign-in/out times if needed</li>
                                    </ol>
                                </Typography>
                                <Box style={{ backgroundColor: '#e3f2fd', padding: 12, marginTop: 8, borderRadius: 4 }}>
                                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>Tip:</Typography>
                                    <Typography variant="body2">
                                        Attendance records help track student presence and can be used for billing and safety purposes.
                                    </Typography>
                                </Box>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                    </Box>
                    )}

                    {/* Student Absences - Only for Admin and Office Manager */}
                    {hasAccess(['admin', 'officemgr']) && (
                    <Box id="student-absences" style={{ scrollMarginTop: 16 }}>
                        <Accordion 
                            expanded={expandedSection === 'student-absences'}
                            onChange={handleAccordionChange('student-absences')}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Student Absences</Typography>
                            </AccordionSummary>
                        <AccordionDetails>
                            <Box>
                                <Typography variant="body1" paragraph>
                                    Record and track student absences for attendance monitoring and communication with parents.
                                </Typography>

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16 }}>
                                    Recording Student Absences
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ol>
                                        <li>Navigate to <strong><Link component={RouterLink} to="/StudentAbsences" onClick={onClose} color="primary">Student Absences</Link></strong></li>
                                        <li>Click <strong>Create</strong> to record a new absence</li>
                                        <li>Select the student and enter the absence date and reason</li>
                                        <li>Click <strong>Save</strong> to record the absence</li>
                                        <li>Edit existing records to update information if needed</li>
                                    </ol>
                                </Typography>
                                <Box style={{ backgroundColor: '#f5f5f5', padding: 12, marginTop: 8, borderRadius: 4 }}>
                                    <Typography variant="body2">
                                        Regular absence tracking helps identify patterns and ensures proper communication with families.
                                    </Typography>
                                </Box>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                    </Box>
                    )}

                    {/* Report Cards - For Admin, Teachers, and Office Manager */}
                    {hasAccess(['admin', 'teacher', 'toddlerteacher', 'officemgr']) && (
                    <Box id="report-cards" style={{ scrollMarginTop: 16 }}>
                        <Accordion 
                            expanded={expandedSection === 'report-cards'}
                            onChange={handleAccordionChange('report-cards')}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Report Cards</Typography>
                            </AccordionSummary>
                        <AccordionDetails>
                            <Box>
                                <Typography variant="body1" paragraph>
                                    View and edit student report cards with progress evaluations and notes.
                                </Typography>

                                {role === 'officemgr' && (
                                    <Box style={{ backgroundColor: '#e3f2fd', padding: 12, marginTop: 8, borderRadius: 4 }}>
                                        <Typography variant="body2" style={{ fontWeight: 'bold' }}>Office Manager Access:</Typography>
                                        <Typography variant="body2">
                                            You can view and edit report cards. Teachers typically complete the evaluations, but you can review and make updates as needed.
                                        </Typography>
                                    </Box>
                                )}

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16 }}>
                                    Viewing and Editing Report Cards
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ol>
                                        <li>Navigate to <strong><Link component={RouterLink} to="/ReportCards" onClick={onClose} color="primary">Report Cards</Link></strong></li>
                                        <li>Filter by Classroom, Semester, and Year to find specific report cards</li>
                                        <li>Click on a report card to view or edit</li>
                                        <li>Update evaluations, ratings, and notes as needed</li>
                                        <li>Click <strong>Save</strong> to save changes</li>
                                    </ol>
                                </Typography>
                                <Box style={{ backgroundColor: '#f5f5f5', padding: 12, marginTop: 8, borderRadius: 4 }}>
                                    <Typography variant="body2">
                                        Report cards are typically generated by Admins from the Students page. Once created, they can be edited by teachers and office managers.
                                    </Typography>
                                </Box>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                    </Box>
                    )}

                    {/* Accident Reports - Only for Admin and Office Manager */}
                    {hasAccess(['admin', 'officemgr']) && (
                    <Box id="accident-reports" style={{ scrollMarginTop: 16 }}>
                        <Accordion 
                            expanded={expandedSection === 'accident-reports'}
                            onChange={handleAccordionChange('accident-reports')}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Accident Reports</Typography>
                            </AccordionSummary>
                        <AccordionDetails>
                            <Box>
                                <Typography variant="body1" paragraph>
                                    Create and manage incident reports for student accidents or injuries that occur at school.
                                </Typography>

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16 }}>
                                    Creating an Accident Report
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ol>
                                        <li>Navigate to <strong><Link component={RouterLink} to="/AccidentReports" onClick={onClose} color="primary">Accident Reports</Link></strong></li>
                                        <li>Click <strong>Create</strong> to start a new report</li>
                                        <li>Fill in the required information:
                                            <ul>
                                                <li>Select the student involved</li>
                                                <li>Enter the date and time of the incident</li>
                                                <li>Describe what happened</li>
                                                <li>Note any injuries and treatment provided</li>
                                                <li>Add witness information if applicable</li>
                                            </ul>
                                        </li>
                                        <li>Click <strong>Save</strong> to create the report</li>
                                    </ol>
                                </Typography>

                                <Divider style={{ margin: '16px 0' }} />

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                    Viewing and Editing Reports
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ol>
                                        <li>View all accident reports in the list</li>
                                        <li>Click on a report to view full details</li>
                                        <li>Edit reports to add updates or corrections</li>
                                        <li>Use filters to find reports by student or date</li>
                                    </ol>
                                </Typography>
                                <Box style={{ backgroundColor: '#ffebee', padding: 12, marginTop: 8, borderRadius: 4 }}>
                                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>Important:</Typography>
                                    <Typography variant="body2">
                                        Accident reports are important legal documents. Complete them accurately and promptly after any incident occurs.
                                    </Typography>
                                </Box>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                    </Box>
                    )}

                    {/* Receipts - Admin Only */}
                    {hasAccess(['admin']) && (
                    <Box id="receipts" style={{ scrollMarginTop: 16 }}>
                        <Accordion 
                            expanded={expandedSection === 'receipts'}
                            onChange={handleAccordionChange('receipts')}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Receipts</Typography>
                            </AccordionSummary>
                        <AccordionDetails>
                            <Box>
                                <Typography variant="body1" paragraph>
                                    Manage payment receipts for student fees, tuition, and other charges.
                                </Typography>

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16 }}>
                                    Viewing Receipts
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ol>
                                        <li>Navigate to <strong><Link component={RouterLink} to="/Receipts" onClick={onClose} color="primary">Receipts</Link></strong></li>
                                        <li>View all receipts with student information and payment details</li>
                                        <li>Use filters to find specific receipts by student, date, or type</li>
                                        <li>Click on a receipt to view full details</li>
                                    </ol>
                                </Typography>

                                <Divider style={{ margin: '16px 0' }} />

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                    Editing Receipts
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ol>
                                        <li>Open a receipt using the <strong>Edit</strong> button</li>
                                        <li>Update payment information, amounts, or status as needed</li>
                                        <li>Mark receipts as paid or update payment dates</li>
                                        <li>Click <strong>Save</strong> to update the receipt</li>
                                    </ol>
                                </Typography>
                                <Box style={{ backgroundColor: '#f5f5f5', padding: 12, marginTop: 8, borderRadius: 4 }}>
                                    <Typography variant="body2">
                                        Receipts are typically generated automatically when payments are processed. You can edit them to correct information or update payment status.
                                    </Typography>
                                </Box>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                    </Box>
                    )}

                    {/* Payments - Admin Only */}
                    {hasAccess(['admin']) && (
                    <Box id="payments" style={{ scrollMarginTop: 16 }}>
                        <Accordion 
                            expanded={expandedSection === 'payments'}
                            onChange={handleAccordionChange('payments')}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Payments</Typography>
                            </AccordionSummary>
                        <AccordionDetails>
                            <Box>
                                <Typography variant="body1" paragraph>
                                    Process and track student payments for tuition, fees, and other charges.
                                </Typography>

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16 }}>
                                    Creating a Payment
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ol>
                                        <li>Navigate to <strong><Link component={RouterLink} to="/Payments" onClick={onClose} color="primary">Payments</Link></strong></li>
                                        <li>Click <strong>Create</strong> to record a new payment</li>
                                        <li>Fill in the payment details:
                                            <ul>
                                                <li>Select the student</li>
                                                <li>Enter payment amount and date</li>
                                                <li>Select payment method</li>
                                                <li>Add notes if needed</li>
                                            </ul>
                                        </li>
                                        <li>Click <strong>Save</strong> to record the payment</li>
                                    </ol>
                                </Typography>

                                <Divider style={{ margin: '16px 0' }} />

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                    Viewing and Editing Payments
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ol>
                                        <li>View all payments in the list</li>
                                        <li>Use filters to find payments by student, date, or amount</li>
                                        <li>Click on a payment to view or edit details</li>
                                        <li>Update payment information or correct errors as needed</li>
                                    </ol>
                                </Typography>
                                <Box style={{ backgroundColor: '#e3f2fd', padding: 12, marginTop: 8, borderRadius: 4 }}>
                                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>Tip:</Typography>
                                    <Typography variant="body2">
                                        Regular payment tracking helps maintain accurate student account balances and financial records.
                                    </Typography>
                                </Box>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                    </Box>
                    )}

                    {/* Master Report Cards - Admin Only */}
                    {hasAccess(['admin']) && (
                    <Box id="master-report-cards" style={{ scrollMarginTop: 16 }}>
                        <Accordion 
                            expanded={expandedSection === 'master-report-cards'}
                            onChange={handleAccordionChange('master-report-cards')}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Master Report Cards</Typography>
                            </AccordionSummary>
                        <AccordionDetails>
                            <Box>
                                <Typography variant="body1" paragraph>
                                    Create and manage templates for report cards. These templates define the evaluation categories and structure used when generating student report cards.
                                </Typography>

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16 }}>
                                    Creating a Master Report Card Template
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ol>
                                        <li>Navigate to <strong><Link component={RouterLink} to="/MasterReportCards" onClick={onClose} color="primary">Master Report Cards</Link></strong></li>
                                        <li>Click <strong>Create</strong> to start a new template</li>
                                        <li>Fill in the template details:
                                            <ul>
                                                <li><strong>Program</strong>: Select Primary or Toddler</li>
                                                <li><strong>Classroom</strong>: Select the classroom (01, 02, 03, or 04)</li>
                                                <li><strong>Categories</strong>: Add evaluation categories (e.g., Language, Math, Social Skills)</li>
                                                <li><strong>Year</strong>: Set the academic year</li>
                                            </ul>
                                        </li>
                                        <li>Click <strong>Save</strong> to create the template</li>
                                    </ol>
                                </Typography>

                                <Divider style={{ margin: '16px 0' }} />

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                    Editing Templates
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ol>
                                        <li>View all master report card templates in the list</li>
                                        <li>Click on a template to edit</li>
                                        <li>Add, remove, or modify evaluation categories</li>
                                        <li>Update template structure as needed</li>
                                        <li>Click <strong>Save</strong> to update the template</li>
                                    </ol>
                                </Typography>
                                <Box style={{ backgroundColor: '#f5f5f5', padding: 12, marginTop: 8, borderRadius: 4 }}>
                                    <Typography variant="body2">
                                        Master Report Card templates are used when generating report cards for students. Each program/classroom combination should have a template.
                                    </Typography>
                                </Box>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                    </Box>
                    )}

                    {/* Toddler Reports - Admin, Toddler Teacher, Toddler Assistant */}
                    {hasAccess(['admin', 'toddlerteacher', 'toddlerassistant']) && (
                    <Box id="toddler-reports" style={{ scrollMarginTop: 16 }}>
                        <Accordion 
                            expanded={expandedSection === 'toddler-reports'}
                            onChange={handleAccordionChange('toddler-reports')}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Toddler Daily Reports</Typography>
                            </AccordionSummary>
                        <AccordionDetails>
                            <Box>
                                <Typography variant="body1" paragraph>
                                    Create and manage daily reports for toddler program students, documenting activities, meals, naps, and other daily information.
                                </Typography>

                                {role === 'toddlerassistant' && (
                                    <Box style={{ backgroundColor: '#e3f2fd', padding: 12, marginTop: 8, borderRadius: 4 }}>
                                        <Typography variant="body2" style={{ fontWeight: 'bold' }}>Toddler Assistant Access:</Typography>
                                        <Typography variant="body2">
                                            You can create and edit toddler daily reports to document student activities and daily care information.
                                        </Typography>
                                    </Box>
                                )}

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16 }}>
                                    Creating a Daily Report
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ol>
                                        <li>Navigate to <strong><Link component={RouterLink} to="/ToddlerReports" onClick={onClose} color="primary">Toddler Reports</Link></strong></li>
                                        <li>Click <strong>Create</strong> to start a new daily report</li>
                                        <li>Fill in the report details:
                                            <ul>
                                                <li>Select the student</li>
                                                <li>Enter the date</li>
                                                <li>Document meals, snacks, and eating habits</li>
                                                <li>Record nap times and duration</li>
                                                <li>Note activities and playtime</li>
                                                <li>Add any special notes or observations</li>
                                            </ul>
                                        </li>
                                        <li>Click <strong>Save</strong> to create the report</li>
                                    </ol>
                                </Typography>

                                <Divider style={{ margin: '16px 0' }} />

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                    Viewing and Editing Reports
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ol>
                                        <li>View all toddler daily reports in the list</li>
                                        <li>Filter by student, date, or classroom to find specific reports</li>
                                        <li>Click on a report to view or edit</li>
                                        <li>Update information or add additional notes as needed</li>
                                        <li>Click <strong>Save</strong> to save changes</li>
                                    </ol>
                                </Typography>
                                <Box style={{ backgroundColor: '#f5f5f5', padding: 12, marginTop: 8, borderRadius: 4 }}>
                                    <Typography variant="body2">
                                        Daily reports help parents stay informed about their child's day at school, including meals, activities, and any special notes.
                                    </Typography>
                                </Box>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                    </Box>
                    )}

                    {/* Employee Hours - Admin Only */}
                    {hasAccess(['admin']) && (
                    <Box id="employee-hours" style={{ scrollMarginTop: 16 }}>
                        <Accordion 
                            expanded={expandedSection === 'employee-hours'}
                            onChange={handleAccordionChange('employee-hours')}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Employee Hours</Typography>
                            </AccordionSummary>
                        <AccordionDetails>
                            <Box>
                                <Typography variant="body1" paragraph>
                                    Track employee sign-in and sign-out times for work hour monitoring and payroll purposes.
                                </Typography>

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16 }}>
                                    Recording Employee Hours
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ol>
                                        <li>Navigate to <strong><Link component={RouterLink} to="/EmployeeSignInOut" onClick={onClose} color="primary">Employee Hours</Link></strong></li>
                                        <li>View existing attendance records or create new entries</li>
                                        <li>Edit records to correct sign-in/out times if needed</li>
                                        <li>Use filters to find records by employee or date</li>
                                    </ol>
                                </Typography>
                                <Box style={{ backgroundColor: '#e3f2fd', padding: 12, marginTop: 8, borderRadius: 4 }}>
                                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>Tip:</Typography>
                                    <Typography variant="body2">
                                        Accurate time tracking helps with payroll processing and ensures compliance with labor regulations.
                                    </Typography>
                                </Box>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                    </Box>
                    )}

                    {/* Chat Queries - Admin Only */}
                    {hasAccess(['admin']) && (
                    <Box id="chat-queries" style={{ scrollMarginTop: 16 }}>
                        <Accordion 
                            expanded={expandedSection === 'chat-queries'}
                            onChange={handleAccordionChange('chat-queries')}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">Chat Queries</Typography>
                            </AccordionSummary>
                        <AccordionDetails>
                            <Box>
                                <Typography variant="body1" paragraph>
                                    View logs of AI agent chat interactions and queries from the school's chat system.
                                </Typography>

                                <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16 }}>
                                    Viewing Chat Query Logs
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ol>
                                        <li>Navigate to <strong><Link component={RouterLink} to="/ChatQueries" onClick={onClose} color="primary">Chat Queries</Link></strong></li>
                                        <li>View all chat interactions and queries</li>
                                        <li>Review questions asked and responses provided by the AI agent</li>
                                        <li>Use filters to find specific queries by date or topic</li>
                                    </ol>
                                </Typography>
                                <Box style={{ backgroundColor: '#f5f5f5', padding: 12, marginTop: 8, borderRadius: 4 }}>
                                    <Typography variant="body2">
                                        Chat query logs help monitor the AI agent's performance and understand common questions from parents and visitors.
                                    </Typography>
                                </Box>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                    </Box>
                    )}

                    </Box>
                </Box>
            </Box>
        </Drawer>
    );
};

export default HelpDialog;

